// =====================================================================
// PDF FIS des courses parallèles télémark — ajouté le 30.09.2026.
//   SL : liste de départ d'un tour des finales (paires, couleurs)
//   RL : liste de résultats avec pénalités (classés + non classés)
// Nom de fichier FIS : SAISON + TM + CODEX + RL/SL (ex: 2026TM8512RL.pdf).
// À placer dans src/utils/ (même dossier que ranking.js).
// =====================================================================
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const PAGE_W = 210;
const MARGIN = 14;
const HEAD = { fillColor: [20, 20, 20], textColor: 255 };
const SUBHEAD = { fillColor: [230, 230, 230], textColor: 0 };

const LEVEL_BY_MATCH_COUNT = { 1: "final", 2: "semifinal", 4: "quarterfinal", 8: "eightfinal", 16: "sixteenfinal", 32: "thirtytwofinal" };
// Tous les libellés des PDF FIS sont en ANGLAIS (documents officiels
// transmis à la FIS) — 01.10.2026. Les messages d'erreur affichés dans
// l'interface restent en français.
const LEVEL_LABEL = {
    final: "Final",
    smallfinal: "Small Final",
    semifinal: "Semifinal",
    quarterfinal: "Quarterfinal",
    eightfinal: "1/8 Final",
    sixteenfinal: "1/16 Final",
    thirtytwofinal: "1/32 Final",
    qualification: "Qualification",
};
const GENDER_LABEL = { W: "Women", M: "Men", A: "Mixed" };
const TYPE_LABEL = {
    official: "Official Results",
    unofficial: "Unofficial Results",
    partial: "Partial Results",
};
const COLOR_LABEL = { red: "Red", blue: "Blue" };

// "TechnicalDelegate" -> "Technical Delegate"
const juryFunctionLabel = (f) => String(f || "").replace(/([a-z])([A-Z])/g, "$1 $2");

export function deriveSeasonFromHeader(rh) {
    if (rh.season) return String(rh.season);
    const y = Number(rh.year);
    const m = Number(rh.month);
    return y && m ? String(m >= 7 ? y + 1 : y) : "";
}

export function fisFileBase(rh, suffix) {
    return `${deriveSeasonFromHeader(rh)}TM${String(rh.codex || "").trim()}${suffix}`;
}

// Sépare "NOM Prénom" (nom en majuscules en tête), comme côté serveur.
function splitName(full) {
    const words = String(full || "").trim().split(/\s+/).filter(Boolean);
    const last = [];
    let i = 0;
    while (i < words.length && words[i] === words[i].toUpperCase() && /[A-ZÀ-ÖØ-Þ]/.test(words[i])) {
        last.push(words[i]);
        i++;
    }
    if (!last.length) return { lastname: words[words.length - 1] || "", firstname: words.slice(0, -1).join(" ") };
    return { lastname: last.join(" "), firstname: words.slice(i).join(" ") };
}
const displayName = (a) => {
    const { lastname, firstname } = splitName(a?.name);
    return [lastname, firstname].filter(Boolean).join(" ");
};

const qualifTime = (q) => q?.time ?? q?.qualifTime ?? q?.totalTime ?? q?.result ?? "";

// Dernier tour atteint par chaque athlète (par fiscode).
export function levelByFiscode(bracket) {
    const out = new Map();
    (bracket?.rounds || []).forEach((round) => {
        const level = LEVEL_BY_MATCH_COUNT[round.length];
        round.forEach((m) => {
            [m.athleteA, m.athleteB].forEach((a) => {
                if (a && !a.bye && a.fiscode) out.set(String(a.fiscode), level);
            });
        });
    });
    const sf = bracket?.smallFinal;
    if (sf) [sf.athleteA, sf.athleteB].forEach((a) => a && a.fiscode && out.set(String(a.fiscode), "smallfinal"));
    return out;
}

// Paires d'un tour (index, ou "small" pour la petite finale).
export function heatsForRound(bracket, roundIndex) {
    const matches = roundIndex === "small" ? (bracket?.smallFinal ? [bracket.smallFinal] : []) : bracket?.rounds?.[roundIndex] || [];
    return matches
        .map((m, idx) => ({
            heat: idx + 1,
            athletes: [
                m.athleteA && !m.athleteA.bye ? { athlete: m.athleteA, order: 1, color: m.colorA } : null,
                m.athleteB && !m.athleteB.bye ? { athlete: m.athleteB, order: 2, color: m.colorB } : null,
            ].filter(Boolean),
        }))
        .filter((h) => h.athletes.length);
}

export function roundLevel(bracket, roundIndex) {
    if (roundIndex === "small") return "smallfinal";
    return LEVEL_BY_MATCH_COUNT[bracket?.rounds?.[roundIndex]?.length] || "";
}

// ---------- logo 321-timing (centre du pied de page) ---------------------
// Fichier servi par le site : <racine>/logo/logo.png. Chargé une fois puis
// gardé en mémoire (data URL) — appeler preloadPdfAssets() avant de
// générer un PDF (fait dans ParallelFisExport.jsx). Si le logo est
// introuvable, le PDF est généré sans, jamais bloqué.
const OWN_LOGO_URL = "/logo/logo.png";
let ownLogoDataUrl = null;

export async function preloadPdfAssets() {
    if (ownLogoDataUrl !== null) return;
    try {
        const res = await fetch(OWN_LOGO_URL);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        ownLogoDataUrl = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    } catch {
        ownLogoDataUrl = ""; // pas de logo, on n'essaie plus
    }
}

// Pour les tests hors navigateur.
export function setOwnLogo(dataUrl) {
    ownLogoDataUrl = dataUrl || "";
}

// ---------- logos (Config impression des parallèles) --------------------
// branding : { bannerImage, logoLeft, logoRight, footerLogo } en data URL
// (PNG/JPEG), mêmes champs que PrintHeader/PrintFooter. Ajouté le
// 01.10.2026 pour la liste de résultats (RL). Une image illisible est
// simplement ignorée, elle ne bloque jamais le PDF.
function fitImage(doc, dataUrl, maxW, maxH) {
    if (!dataUrl) return null;
    try {
        const p = doc.getImageProperties(dataUrl);
        const ratio = p.width / p.height;
        // Le plus grand possible dans maxW x maxH, proportions conservées.
        let w = maxW;
        let h = w / ratio;
        if (h > maxH) {
            h = maxH;
            w = h * ratio;
        }
        return { w, h };
    } catch {
        return null;
    }
}

function drawImage(doc, dataUrl, x, y, size) {
    try {
        doc.addImage(dataUrl, x, y, size.w, size.h);
    } catch {
        /* format non supporté : ignoré */
    }
}

// Marge basse des tableaux : laisse la place au pied de page (et au logo
// de pied de page s'il y en a un) sur toutes les pages.
function bottomMargin(doc) {
    return doc.__bottomMargin || 16;
}

// ---------- en-tête / pied de page --------------------------------------
function header(doc, fisConfig, subtitle, branding) {
    const rh = fisConfig.raceheader || {};

    // Bandeau sur TOUTE la largeur de la page (bord à bord, comme
    // PrintHeader), hauteur selon ses proportions (plafonnée à 50 mm),
    // puis logos gauche/droite à hauteur du titre.
    let offset = 0;
    const banner = fitImage(doc, branding?.bannerImage, PAGE_W, 50);
    if (banner) {
        // Toujours étiré à la largeur de la page si la hauteur le permet.
        drawImage(doc, branding.bannerImage, (PAGE_W - banner.w) / 2, 0, banner);
        offset = banner.h - 2;
    }
    const left = fitImage(doc, branding?.logoLeft, 38, 16);
    if (left) drawImage(doc, branding.logoLeft, MARGIN, 7 + offset, left);
    const right = fitImage(doc, branding?.logoRight, 38, 16);
    if (right) drawImage(doc, branding.logoRight, PAGE_W - MARGIN - right.w, 7 + offset, right);

    doc.setFont(undefined, "bold");
    doc.setFontSize(13);
    doc.text(rh.eventname || "Telemark", PAGE_W / 2, 13 + offset, { align: "center" });
    doc.setFontSize(11);
    doc.text(
        [rh.discipline, GENDER_LABEL[rh.gender]].filter(Boolean).join(" — "),
        PAGE_W / 2,
        19 + offset,
        { align: "center" }
    );
    doc.setFontSize(10);
    doc.text(subtitle, PAGE_W / 2, 25 + offset, { align: "center" });
    doc.setFont(undefined, "normal");
    doc.setFontSize(8);
    const date = rh.day && rh.month && rh.year ? `${String(rh.day).padStart(2, "0")}.${String(rh.month).padStart(2, "0")}.${rh.year}` : "";
    doc.text([rh.place, date].filter(Boolean).join(", "), MARGIN, 31 + offset);
    doc.text(
        `${rh.nation || ""} — Codex ${rh.codex || ""} — Season ${deriveSeasonFromHeader(rh)} — ${rh.category || ""}`,
        PAGE_W - MARGIN,
        31 + offset,
        { align: "right" }
    );
    doc.line(MARGIN, 33 + offset, PAGE_W - MARGIN, 33 + offset);

    // Réserve la place du logo de pied de page pour tous les tableaux.
    const footerLogo = fitImage(doc, branding?.footerLogo, PAGE_W - 2 * MARGIN, 14);
    doc.__footerLogo = footerLogo ? { dataUrl: branding.footerLogo, size: footerLogo } : null;
    doc.__bottomMargin = 16 + (footerLogo ? footerLogo.h + 3 : 0);

    return 38 + offset;
}

function footer(doc, fisConfig) {
    const ri = fisConfig.raceinfo || {};
    const n = doc.getNumberOfPages();
    for (let i = 1; i <= n; i++) {
        doc.setPage(i);
        const h = doc.internal.pageSize.getHeight();
        doc.setFontSize(7);
        if (doc.__footerLogo) {
            const { dataUrl, size } = doc.__footerLogo;
            drawImage(doc, dataUrl, (PAGE_W - size.w) / 2, h - 13 - size.h, size);
        }
        doc.line(MARGIN, h - 12, PAGE_W - MARGIN, h - 12);
        // Pied de page (01.10.2026) : à gauche le chronométrage et le
        // traitement des données (321-timing.ch), au centre le logo
        // 321-timing, à droite le logiciel et la pagination.
        doc.text("Timing: 321-timing.ch", MARGIN, h - 8);
        doc.text("Data processing: 321-timing.ch", MARGIN, h - 5);
        const own = fitImage(doc, ownLogoDataUrl, 30, 8);
        if (own) drawImage(doc, ownLogoDataUrl, (PAGE_W - own.w) / 2, h - 11, own);
        doc.text(
            [ri.softwarename || "Parallel Soft", ri.softwareversion || "1.0"].join(" "),
            PAGE_W - MARGIN,
            h - 8,
            { align: "right" }
        );
        doc.text(`Page ${i}/${n}`, PAGE_W - MARGIN, h - 5, { align: "right" });
    }
}

// Jury (gauche) + parcours/météo de la manche 1 (droite).
function raceSheet(doc, y, fisConfig) {
    const plain = { theme: "plain", styles: { fontSize: 8, cellPadding: 1 }, headStyles: { fontStyle: "bold" } };
    const jury = (fisConfig.jury || []).filter((j) => j.lastname || j.firstname);
    const run = (fisConfig.raceinfo?.runs || [])[0] || {};
    const c = run.course || {};
    const w = run.weather || {};
    const setter = c.coursesetter || {};
    const person = (p) => (p && (p.lastname || p.firstname) ? `${[p.lastname, p.firstname].filter(Boolean).join(" ")}${p.nation ? ` (${p.nation})` : ""}` : "—");

    autoTable(doc, {
        ...plain,
        startY: y,
        margin: { left: MARGIN, right: PAGE_W / 2 + 2 },
        head: [["JURY", ""]],
        body: jury.length ? jury.map((j) => [juryFunctionLabel(j.function), person(j)]) : [["—", ""]],
    });
    const y1 = doc.lastAutoTable.finalY;

    const drop = Number(c.startelev) && Number(c.finishelev) ? `${Number(c.startelev) - Number(c.finishelev)} m` : "—";
    const forerunners = (c.forerunners || []).filter((f) => f.lastname || f.firstname).map(person).join("\n");
    autoTable(doc, {
        ...plain,
        startY: y,
        margin: { left: PAGE_W / 2 + 2, right: MARGIN },
        head: [["COURSE", ""]],
        body: [
            ["Course name", c.name || "—"],
            ["Homologation", c.homologation || "—"],
            ["Start / finish", Number(c.startelev) || Number(c.finishelev) ? `${c.startelev || "—"} m / ${c.finishelev || "—"} m` : "—"],
            ["Vertical drop", drop],
            ["Length", Number(c.length) ? `${c.length} m` : "—"],
            ["Gates / turning gates", Number(c.gates) || Number(c.turninggates) ? `${c.gates || 0} / ${c.turninggates || 0}` : "—"],
            ["Course setter", person(setter)],
            ...(forerunners ? [["Forerunners", forerunners]] : []),
            ["Start time", run.starttime || "—"],
            ["Weather / snow", [w.weather, w.snow].filter(Boolean).join(" / ") || "—"],
            [
                "Air temperature",
                w.temperatureair != null && String(w.temperatureair).trim() !== ""
                    ? `${String(w.temperatureair).replace(/\s*°\s*C?\s*$/i, "").trim()} °C`
                    : "—",
            ],
        ],
    });
    return Math.max(y1, doc.lastAutoTable.finalY) + 4;
}

function sectionTitle(doc, y, text) {
    if (y > doc.internal.pageSize.getHeight() - bottomMargin(doc) - 14) {
        doc.addPage();
        y = 20;
    }
    doc.setFontSize(10);
    doc.setFont(undefined, "bold");
    doc.text(text, MARGIN, y);
    doc.setFont(undefined, "normal");
    return y + 2;
}

// Termine le PDF : téléchargement (save = true, défaut) ou data URL pour
// l'envoi par email/API. Renvoie toujours { filename, dataUrl? }.
function finish(doc, filename, save) {
    if (save) {
        doc.save(filename);
        return { filename };
    }
    return { filename, dataUrl: doc.output("datauristring") };
}

// ---------- SL : liste de départ d'un tour ------------------------------
export function startlistPdf({ fisConfig, bracket, roundIndex, save = true }) {
    const rh = fisConfig.raceheader || {};
    const level = roundLevel(bracket, roundIndex);
    const heats = heatsForRound(bracket, roundIndex);
    if (!heats.length) throw new Error("Ce tour n'a pas encore d'athlètes (tour précédent non terminé).");

    const doc = new jsPDF();
    let y = header(doc, fisConfig, `Start List — ${LEVEL_LABEL[level] || "Finals"}`);
    y = raceSheet(doc, y, fisConfig);

    const body = [];
    heats.forEach((h) => {
        h.athletes.forEach(({ athlete, order, color }, i) => {
            body.push([
                i === 0 ? h.heat : "",
                order,
                color ? COLOR_LABEL[color] || color : "",
                athlete.bib ?? "",
                athlete.fiscode ?? "",
                displayName(athlete),
                athlete.birthyear ?? "",
                athlete.nation ?? "",
                athlete.qualifRank ?? athlete.rank ?? "",
            ]);
        });
        if (h.athletes.length === 1) body.push(["", "", "", "", "", "(bye)", "", "", ""]);
    });

    autoTable(doc, {
        startY: y,
        margin: { left: MARGIN, right: MARGIN, bottom: bottomMargin(doc) },
        styles: { fontSize: 8 },
        headStyles: HEAD,
        head: [["Heat", "Order", "Colour", "Bib", "FIS Code", "Name", "YB", "NSA", "Qual. Rank"]],
        body,
        didParseCell: (data) => {
            // Ligne de séparation visuelle entre les paires.
            if (data.section === "body" && data.row.raw[0] !== "" && data.row.index > 0) data.cell.styles.lineWidth = { top: 0.3 };
        },
    });

    footer(doc, fisConfig);
    return finish(doc, `${fisFileBase(rh, "SL")}.pdf`, save);
}

// ---------- RL : liste de résultats avec pénalités ----------------------
// ranking : sortie de deriveRanking(bracket) (place, bib, name, fiscode...)
// qualifiers : qualifications (avec fisStatus) de /api/finals/report-data
// branding (optionnel) : logos de la Config impression de la catégorie.
export function resultListPdf({ fisConfig, bracket, ranking, qualifiers, branding = null, save = true }) {
    const rh = fisConfig.raceheader || {};
    const ri = fisConfig.raceinfo || {};
    const levels = levelByFiscode(bracket);
    const qualByFiscode = new Map((qualifiers || []).map((q) => [String(q.fiscode), q]));

    const doc = new jsPDF();
    let y = header(doc, fisConfig, TYPE_LABEL[rh.type] || "Results", branding);
    y = raceSheet(doc, y, fisConfig);

    // Pénalités et liste FIS utilisée (liste "with penalties" demandée par la FIS).
    doc.setFontSize(8);
    doc.text(
        `FIS list no. ${ri.usedfislist || "—"}    Applied penalty: ${ri.appliedpenalty || "0.00"}    ` +
        `Calculated penalty: ${ri.calculatedpenalty || "0.00"}    F-value: ${ri.fvalue || "0"}`,
        MARGIN,
        y
    );
    y += 5;

    const rankedFiscodes = new Set((ranking || []).map((r) => String(r.fiscode)));
    const notClassified = (qualifiers || [])
        .filter((q) => q.fiscode && !rankedFiscodes.has(String(q.fiscode)))
        .map((q) => ({ ...q, status: q.fisStatus || "DNQ" }));
    const nations = new Set([...(ranking || []), ...notClassified].map((a) => a.nation).filter(Boolean));
    doc.text(
        `Number of competitors: ${(ranking || []).length + notClassified.length}, ranked: ${(ranking || []).length}, ` +
        `number of NSA: ${nations.size}`,
        MARGIN,
        y
    );
    y += 4;

    autoTable(doc, {
        startY: y,
        margin: { left: MARGIN, right: MARGIN, bottom: bottomMargin(doc) },
        styles: { fontSize: 8 },
        headStyles: HEAD,
        head: [["Rank", "Bib", "FIS Code", "Name", "YB", "NSA", "Phase reached", "Qual. Rank", "Qual. Time"]],
        body: (ranking || []).map((r) => {
            const q = qualByFiscode.get(String(r.fiscode));
            return [
                r.place,
                r.bib ?? "",
                r.fiscode ?? "",
                displayName(r),
                r.birthyear ?? q?.birthyear ?? "",
                r.nation ?? "",
                LEVEL_LABEL[levels.get(String(r.fiscode))] || "",
                r.qualifRank ?? q?.rank ?? "",
                qualifTime(q) || "",
            ];
        }),
    });
    y = doc.lastAutoTable.finalY + 8;

    // Non classés : DNQ d'abord (ordre de qualification), puis DNS/DNF/DSQ.
    const groups = [
        ["DNQ", "Did not qualify for finals (DNQ)"],
        ["DNF", "Did not finish qualification (DNF)"],
        ["DSQ", "Disqualified in qualification (DSQ)"],
        ["DNS", "Did not start (DNS)"],
    ];
    groups.forEach(([status, label]) => {
        const list = notClassified.filter((n) => n.status === status);
        if (!list.length) return;
        y = sectionTitle(doc, y, `${label} — ${list.length}`);
        autoTable(doc, {
            startY: y,
            margin: { left: MARGIN, right: MARGIN, bottom: bottomMargin(doc) },
            styles: { fontSize: 8 },
            headStyles: SUBHEAD,
            head: [["Bib", "FIS Code", "Name", "YB", "NSA", "Qual. Rank", "Qual. Time"]],
            body: list.map((n) => [n.bib ?? "", n.fiscode ?? "", displayName(n), n.birthyear ?? "", n.nation ?? "", n.rank ?? "", qualifTime(n) || ""]),
        });
        y = doc.lastAutoTable.finalY + 6;
    });

    footer(doc, fisConfig);
    return finish(doc, `${fisFileBase(rh, "RL")}.pdf`, save);
}